import { useEffect } from 'react';
import { SidebarNav } from 'calc-sidebar';
import type { SidebarItemDef } from 'calc-sidebar';
import { useStore } from '@/store';
import type { ScreenName } from '@bioavail/shared-types';
import { CalculatorScreen } from '@/components/calculator/CalculatorScreen';
import { AnalysisScreen } from '@/components/analysis/AnalysisScreen';
import { ChartsScreen } from '@/components/charts/ChartsScreen';
import { StudiesScreen } from '@/components/studies/StudiesScreen';
import { ReferenceScreen } from '@/components/reference/ReferenceScreen';
import { SettingsScreen } from '@/components/settings/SettingsScreen';
import { ErrorBoundary } from '@/components/ui/ErrorBoundary';
import { TopBar } from './TopBar';

const SIDEBAR_SECTION = 'nav';

const SIDEBAR_ITEMS: SidebarItemDef[] = [
  { id: 'calculator', label: 'Calculator', content: <ErrorBoundary fallbackLabel="Calculator"><CalculatorScreen /></ErrorBoundary> },
  { id: 'analysis', label: 'Analysis', content: <ErrorBoundary fallbackLabel="Analysis"><AnalysisScreen /></ErrorBoundary> },
  { id: 'charts', label: 'Charts', content: <ErrorBoundary fallbackLabel="Charts"><ChartsScreen /></ErrorBoundary> },
  { id: 'studies', label: 'Studies', content: <ErrorBoundary fallbackLabel="Studies"><StudiesScreen /></ErrorBoundary> },
  { id: 'reference', label: 'Reference', content: <ErrorBoundary fallbackLabel="Reference"><ReferenceScreen /></ErrorBoundary> },
  { id: 'settings', label: 'Settings', content: <ErrorBoundary fallbackLabel="Settings"><SettingsScreen /></ErrorBoundary> },
];

/**
 * App shell layout using calc-sidebar's SidebarNav.
 *
 * All six top-level screens are always mounted (hidden via the `hidden`
 * attribute) so component state survives navigation (UX Invariant #8).
 * The sidebar's URL-hash persistence (#nav/<screen>) drives navigation;
 * the store's setActiveScreen mirrors the hash for programmatic nav.
 */
export function AppLayout() {
  const setActiveScreen = useStore((s) => s.setActiveScreen);

  useEffect(() => {
    const syncFromHash = () => {
      const hash = window.location.hash.slice(1);
      const slashIdx = hash.indexOf('/');
      if (slashIdx !== -1) {
        const section = hash.slice(0, slashIdx);
        const item = hash.slice(slashIdx + 1);
        if (section === SIDEBAR_SECTION) {
          setActiveScreen(item as ScreenName);
        }
      }
    };
    window.addEventListener('popstate', syncFromHash);
    return () => window.removeEventListener('popstate', syncFromHash);
  }, [setActiveScreen]);

  return (
    <div className="app-shell">
      <a href="#main-content" className="skip-link">
        Skip to content
      </a>
      <TopBar />
      <div className="app-body app-body--sidebar">
        <SidebarNav
          section={SIDEBAR_SECTION}
          items={SIDEBAR_ITEMS}
          defaultItem="calculator"
          stickyTop="var(--topbar-height)"
          sidebarWidth="var(--sidebar-width)"
          ariaLabel="Primary navigation"
        />
      </div>
    </div>
  );
}
